import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTarget } from "../data/targets";
import Tooltip from "../components/Tooltip";
import LeadershipGauges from "../components/LeadershipGauges";

const C = {
  bg: "#0a0a0a",
  card: "#111",
  border: "rgba(255,255,255,0.08)",
  gold: "#C9A84C",
  teal: "#00D4C8",
  violet: "#7C5CFC",
  white: "#F2F6FA",
  muted: "#888780",
  fog: "#6B7E91",
  red: "#E84040",
  amber: "#E8A020",
  green: "#2EC8A0",
};

function fmtCur(n, cur = "USD") {
  if (!Number.isFinite(n) || n <= 0) return "$0";
  const sym = { USD: "$", INR: "\u20B9", AED: "AED\u00A0", GBP: "\u00A3" }[cur] || "$";
  if (cur === "INR") {
    if (n >= 1e7) return `${sym}${(n / 1e7).toFixed(1)}Cr`;
    if (n >= 1e5) return `${sym}${(n / 1e5).toFixed(1)}L`;
    return `${sym}${n.toLocaleString()}`;
  }
  if (n >= 1e9) return `${sym}${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${sym}${(n / 1e6).toFixed(0)}M`;
  if (n >= 1e3) return `${sym}${(n / 1e3).toFixed(0)}K`;
  return `${sym}${n.toLocaleString()}`;
}

function section() {
  return {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "32px 28px",
    marginBottom: 24,
  };
}

function STitle({ title, subtitle, tip }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: C.white, margin: 0 }}>
        {title}
      </h3>
      {subtitle && (
        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5, letterSpacing: "0.04em" }}>
          {subtitle} {tip && <Tooltip text={tip} />}
        </p>
      )}
    </div>
  );
}

function Gauge({ score }) {
  const r = 80;
  const sw = 10;
  const cx = 100;
  const cy = 100;
  const startAngle = 135;
  const endAngle = 405;
  const totalAngle = endAngle - startAngle;
  const scoreAngle = startAngle + (score / 100) * totalAngle;
  function arc(s, e, radius) {
    const sa = (s * Math.PI) / 180;
    const ea = (e * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(sa);
    const y1 = cy + radius * Math.sin(sa);
    const x2 = cx + radius * Math.cos(ea);
    const y2 = cy + radius * Math.sin(ea);
    const large = e - s > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`;
  }
  const col = score >= 70 ? C.red : score >= 40 ? C.amber : C.green;
  return (
    <svg viewBox="0 0 200 160" style={{ width: "100%", maxWidth: 260 }}>
      <path d={arc(startAngle, endAngle, r)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw} strokeLinecap="round" />
      {score > 0 && <path d={arc(startAngle, scoreAngle, r)} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round" />}
      <text x={cx} y={cy - 4} textAnchor="middle" fill={C.gold} fontSize="42" fontFamily="'Bebas Neue', sans-serif" letterSpacing="0.04em">{score}</text>
      <text x={cx} y={cy + 16} textAnchor="middle" fill={C.muted} fontSize="12" fontFamily="'Overpass Mono', monospace">/ 100</text>
    </svg>
  );
}

function gapColor(g) {
  if (g === "critical") return C.red;
  if (g === "significant") return C.amber;
  return C.teal;
}

function themeColor(t) {
  const lower = t.toLowerCase();
  if (lower.includes("good") || lower.includes("great") || lower.includes("benefit"))
    return { bg: `${C.green}18`, border: `${C.green}40`, text: C.green };
  if (lower.includes("slow") || lower.includes("limited") || lower.includes("lack") || lower.includes("poor"))
    return { bg: `${C.red}18`, border: `${C.red}40`, text: C.red };
  return { bg: `${C.amber}18`, border: `${C.amber}40`, text: C.amber };
}

export default function TargetDueDiligence() {
  const navigate = useNavigate();
  const fundSlug = sessionStorage.getItem("fund_slug") || "khazanah";
  const target = getTarget(fundSlug);
  const [mode, setMode] = useState("preloi");
  const [showDRBanner, setShowDRBanner] = useState(false);
  const [targetName, setTargetName] = useState(target.name);

  const t = target;
  const ebitda = t.ebitda_opportunity;
  const sorted = [...t.automation_risk].sort((a, b) => b.risk_pct - a.risk_pct);
  const highRisk = t.automation_risk.filter((r) => r.level === "high");
  const highPctOfLabour = t.estimated_labour_cost > 0
    ? Math.round((highRisk.length / t.automation_risk.length) * 100)
    : 0;

  const oppLabel =
    t.readiness_score >= 70
      ? "Critical opportunity \u2014 immediate action creates highest EBITDA impact"
      : t.readiness_score >= 40
      ? "Significant opportunity \u2014 structured intervention recommended"
      : "Moderate opportunity \u2014 monitor trajectory";
  const oppColor = t.readiness_score >= 70 ? C.red : t.readiness_score >= 40 ? C.amber : C.green;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.white, fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "48px 28px 0" }}>
        <button onClick={() => navigate("/dashboard")} style={{ background: "none", border: "none", color: C.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 20, letterSpacing: "0.02em" }}>
          ← Back to portfolio
        </button>
        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, margin: "0 0 6px", letterSpacing: "0.02em" }}>Target Due Diligence</h1>
        <p style={{ fontSize: 14, color: C.muted, margin: "0 0 24px" }}>
          Pre-acquisition workforce intelligence built from public data only
        </p>

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20 }}>
          <button
            onClick={() => { setMode("preloi"); setShowDRBanner(false); }}
            style={{
              padding: "10px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              borderRadius: "8px 0 0 8px", border: `1px solid ${C.gold}`,
              background: mode === "preloi" ? C.gold : "transparent",
              color: mode === "preloi" ? C.bg : C.gold,
            }}
          >
            ● Pre-LOI Mode
          </button>
          <button
            onClick={() => { setMode("dataroom"); setShowDRBanner(true); }}
            style={{
              padding: "10px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              borderRadius: "0 8px 8px 0", border: `1px solid ${C.gold}`, borderLeft: "none",
              background: mode === "dataroom" ? C.gold : "transparent",
              color: mode === "dataroom" ? C.bg : C.gold,
            }}
          >
            Data Room Mode <Tooltip text="Unlock precise analysis by sharing your workforce data directly. Data room mode removes directional estimates and replaces with company-specific ASBC calculation, verified leadership profiles and confirmed succession mapping." />
          </button>
        </div>

        {showDRBanner && (
          <div style={{ background: "#111", border: `1px solid ${C.gold}`, borderRadius: 10, padding: "16px 22px", marginBottom: 24 }}>
            <p style={{ fontSize: 14, color: C.gold, margin: 0, lineHeight: 1.5 }}>
              Data Room mode unlocks with NDA and CHRO data upload. Contact reAImagination to activate.
            </p>
          </div>
        )}

        {mode === "preloi" && (
          <div style={{ marginBottom: 32 }}>
            <input
              type="text"
              value={targetName}
              onChange={(e) => setTargetName(e.target.value)}
              placeholder="Enter target company name..."
              style={{
                width: "100%", padding: "14px 18px", fontSize: 15,
                background: "transparent", border: `1px solid ${C.gold}`,
                borderRadius: 8, color: C.white, outline: "none",
              }}
            />
            <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.fog, marginTop: 8, letterSpacing: "0.04em" }}>
              Analysis built from public signals only. No engagement with the target company required.
            </p>
          </div>
        )}
      </div>

      {mode === "preloi" && (
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 28px 96px" }}>

          {/* Panel 1 — Target Snapshot */}
          <div style={section()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
              <div>
                <h3 style={{ fontSize: 24, fontWeight: 700, color: C.white, margin: "0 0 4px" }}>{t.name}</h3>
                <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>{t.sector} · {t.country}</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", background: `${C.amber}18`, border: `1px solid ${C.amber}40`, borderRadius: 99, fontSize: 11, fontWeight: 600, color: C.amber, letterSpacing: "0.08em" }}>
                DIRECTIONAL ESTIMATE
                <Tooltip text="Built from public data only. Accuracy improves significantly with data room access. Typical accuracy: \u00b125-35%." />
              </div>
            </div>

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 20 }}>
              {[
                ["Headcount", t.headcount.toLocaleString(), null],
                ["Est. Labour Cost", fmtCur(t.estimated_labour_cost, t.currency), null],
                ["Est. ASBC Gap", fmtCur(t.asbc_gap_estimate, t.currency), "Estimated ASBC transformation gap based on sector benchmarks and public workforce signals. Requires data room access for precise calculation."],
              ].map(([l, v, tip]) => (
                <div key={l}>
                  <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.1em" }}>{l} {tip && <Tooltip text={tip} />}</p>
                  <p style={{ fontSize: 22, fontWeight: 700, color: C.white, margin: 0 }}>{v}</p>
                </div>
              ))}
              <div>
                <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.1em" }}>EBITDA Opportunity <Tooltip text="Estimated recoverable margin = ASBC gap × 0.6. Directional estimate based on sector benchmarks." /></p>
                <p style={{ fontSize: 28, fontWeight: 700, color: C.gold, margin: 0 }}>{fmtCur(ebitda, t.currency)}</p>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {t.data_sources.map((s) => (
                <span key={s} style={{ padding: "4px 10px", background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, borderRadius: 99, fontSize: 11, color: C.fog }}>{s}</span>
              ))}
            </div>
          </div>

          {/* Panel 2 — Automation Risk */}
          <div style={section()}>
            <STitle title="Automation Risk by Function" subtitle="Estimated % of function tasks addressable by current AI tools" tip="Based on OCF task analysis and sector AI adoption rates." />
            {sorted.map((r) => {
              const col = r.level === "high" ? C.red : r.level === "medium" ? C.amber : C.teal;
              return (
                <div key={r.function} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 13, color: C.white, width: 160, flexShrink: 0 }}>{r.function}</span>
                  <div style={{ flex: 1, height: 10, background: "#1a1a1a", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ width: `${r.risk_pct}%`, height: "100%", background: col, borderRadius: 5, transition: "width 0.4s ease" }} />
                  </div>
                  <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: col, width: 36, textAlign: "right" }}>{r.risk_pct}%</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", padding: "3px 8px", borderRadius: 4, background: `${col}18`, color: col, border: `1px solid ${col}40`, textTransform: "uppercase" }}>
                    {r.level}
                    <Tooltip text={r.level === "high" ? "More than 60% of tasks in this function are addressable by current AI — immediate restructuring opportunity." : r.level === "medium" ? "30-60% addressable. Plan structured transition over 12-24 months." : "Below 30% addressable. Monitor AI capability advancement in this domain."} width={260} />
                  </span>
                </div>
              );
            })}
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: C.gold, marginTop: 16, fontStyle: "italic", lineHeight: 1.5 }}>
              {t.automation_insight || `${highRisk.map((r) => r.function).join(" and ")} represent ~${highPctOfLabour}% of estimated labour cost at high automation risk`}
            </p>
          </div>

          {/* Panel 3 — Leadership */}
          <div style={section()}>
            <STitle title="Leadership Scorecard" subtitle="Scored from public signals: LinkedIn profiles, regulatory filings, job posting patterns, Glassdoor data, and news analysis" tip="Directional estimate — refine with data room access." />
            <LeadershipGauges scores={t.leadership_scores} companyName={t.name} />
          </div>

          {/* Panel 4 — Talent Gaps */}
          <div style={section()}>
            <STitle title="Critical Capability Gaps" subtitle="Identified from job posting patterns, LinkedIn hiring signals, and sector benchmark comparison" tip="Gaps indicate capabilities the target may need to acquire or build." />
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {t.talent_gaps.map((g) => {
                const col = gapColor(g.gap);
                return (
                  <div key={g.capability} style={{ borderLeft: `3px solid ${col}`, padding: "14px 18px", background: "#0d0d0d", borderRadius: "0 8px 8px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.white }}>{g.capability}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", padding: "3px 8px", borderRadius: 4, background: `${col}18`, color: col, border: `1px solid ${col}40`, textTransform: "uppercase" }}>{g.gap}</span>
                    </div>
                    <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.5 }}>{g.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Panel 5 — Key Person */}
          <div style={section()}>
            <STitle title="Key Person & Succession Risk" subtitle="Concentration of institutional knowledge and leadership continuity risk" tip="Concentration of institutional knowledge and leadership continuity risk. Higher score = greater dependency on individuals. Scored from C-suite tenure data, succession visibility and recent leadership transitions." />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 20 }}>
              <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: C.gold, margin: 0, lineHeight: 1 }}>{t.key_person_risk.score}</p>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted, marginTop: 4 }}>/ 10 dependency risk</p>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.fog, marginTop: 2 }}>Higher = greater concentration risk</p>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, maxWidth: 540, marginLeft: "auto", marginRight: "auto" }}>
              {t.key_person_risk.flags.map((f, i) => (
                <li key={i} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 14, color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}>
                  <span style={{ color: C.amber, flexShrink: 0 }}>&#9888;</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Panel 6 — Organisation Health */}
          <div style={section()}>
            <STitle title="Organisation Health Signals" subtitle="Sourced from Glassdoor, Indeed, and LinkedIn" tip="Sourced from Glassdoor, Indeed and LinkedIn employee reviews. Glassdoor rating, CEO approval and recommendation rate are directional signals of cultural health and employee sentiment." />
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 24 }}>
              {[
                ["Glassdoor Rating", `${t.glassdoor.rating} / 5.0`],
                ["CEO Approval", `${t.glassdoor.ceo_approval}%`],
                ["Would Recommend", `${t.glassdoor.recommend_pct}%`],
              ].map(([l, v]) => (
                <div key={l} style={{ background: "#0d0d0d", border: `1px solid ${C.border}`, borderRadius: 10, padding: "18px 22px", flex: 1, minWidth: 160 }}>
                  <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.1em" }}>{l}</p>
                  <p style={{ fontSize: 28, fontWeight: 700, color: C.gold, margin: 0 }}>{v}</p>
                </div>
              ))}
            </div>
            <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginBottom: 12, letterSpacing: "0.04em" }}>
              Key themes from employee reviews:
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {t.glassdoor.review_themes.map((th) => {
                const tc = themeColor(th);
                return (
                  <span key={th} style={{ padding: "5px 12px", borderRadius: 99, fontSize: 12, fontWeight: 500, background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text }}>{th}</span>
                );
              })}
            </div>
          </div>

          {/* Panel 7 — Transformation Opportunity */}
          <div style={section()}>
            <STitle title="Transformation Opportunity Score" subtitle="Estimated value creation potential from workforce restructuring. Higher score = larger unrealised opportunity." tip="Same composite methodology as Company Deep Dive. Higher = larger unrealised value creation opportunity from workforce transformation." />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Gauge score={t.readiness_score} />
              <p style={{ fontSize: 15, fontWeight: 600, color: oppColor, marginTop: 12, textAlign: "center", maxWidth: 400, lineHeight: 1.5 }}>
                {oppLabel}
              </p>
            </div>

            {/* CTA */}
            <div style={{ marginTop: 32, border: `1px solid ${C.gold}`, borderRadius: 12, padding: "24px 28px", textAlign: "center" }}>
              <p style={{ fontSize: 17, fontWeight: 700, color: C.white, margin: "0 0 8px" }}>Ready to move to Data Room analysis?</p>
              <p style={{ fontSize: 13, color: C.muted, margin: "0 0 20px", lineHeight: 1.5 }}>
                Upload NDA and target financials to unlock precise task-level ASBC analysis, confirmed leadership profiles, and board-ready reporting.
              </p>
              <button
                onClick={() => { setMode("dataroom"); setShowDRBanner(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{ padding: "12px 24px", background: "transparent", border: `1px solid ${C.gold}`, color: C.gold, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", letterSpacing: "0.02em" }}
              >
                Activate Data Room →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
